/**
 * 데이터 정의 ↔ 데이터 노드 **연결**.
 *
 * 데이터 노드(SAM파일/DB테이블)는 `config.dataDefinitionId` 로 데이터 정의를 가리킨다. 처음 설계부터
 * 그것이 취지였다 — 정의 id 가 필수 속성이고, 없으면 노드에 경고가 뜬다(2c58bc8). 그런데 실제로는
 * 노드를 만들 때 한 번 **복사**만 하고, 정의를 고치거나 레이아웃을 갱신해도 노드에는 반영되지 않았다
 * (현장 2026-09-22: "원래 처음부터 의도와 달리 만들어진 듯함").
 *
 * 여기서 정하는 것:
 *   · 정의에서 오는 칸(경로·구분자·테이블·컬럼 …)은 **정의가 주인**이다 — 노드는 그 값을 따른다
 *   · 그 밖의 노드 설정(이름, LOB 처리 방식 등)은 노드의 것이다 — 건드리지 않는다
 *   · 노드에는 정의 id 만 저장하고, 값은 쓸 때 정의에서 읽는다(현장 2026-09-22) — 저장소 경계에서 채우고 걷어 내는
 *     일은 definitionRefs.ts, 서버 변환은 backend services/definition_refs.py 가 맡는다
 *
 * 처음에는 정의가 바뀔 때 값을 저장된 캔버스마다 새 버전으로 퍼뜨렸다(propagate). 워크플로마다 사본이 생기고
 * 퍼뜨리기 전의 캔버스가 옛 값을 들고 있어서 참조 방식으로 바꿨다. 여기 남은 것은 비교(isOutOfSync — 예전에
 * 값을 복사해 둔 노드를 알아보는 데 쓴다)와 정의를 쓰는 곳 찾기(findDefinitionUses)다.
 */
import type { DataDefinition } from '../../../store/dataDefinitionsSlice';
import { nodeFromDataDefinition } from './dataDefinitionNode';
import * as repo from '../../../services/repo';

/** 정의가 주인인 칸 — 이 칸들만 비교하고 덮어쓴다 */
const LINKED_FIELDS: Record<'csvData' | 'rdbData', string[]> = {
  csvData: ['filepath', 'delimiter', 'hasHeader', 'format', 'encoding', 'fieldWidths', 'fieldNames', 'tableColumns'],
  rdbData: ['connection', 'schema', 'table', 'columns', 'tableColumns'],
};

const isDataNode = (n: any): n is { type: 'csvData' | 'rdbData'; data?: any } => n?.type === 'csvData' || n?.type === 'rdbData';

/** 컬럼 비교는 이름·자료형만 — 노드에서 붙인 설명(comment)까지 다르다고 보면 쓸데없이 새 버전이 생긴다 */
function linkedView(type: 'csvData' | 'rdbData', cfg: any): string {
  const pick: Record<string, any> = {};
  for (const k of LINKED_FIELDS[type]) {
    const v = cfg?.[k];
    pick[k] = k === 'tableColumns' && Array.isArray(v) ? v.map((c: any) => [c?.name, c?.dataType]) : v ?? null;
  }
  return JSON.stringify(pick);
}

/** 노드 설정에 정의를 입힌다 — 정의가 주인인 칸만 바뀌고 나머지 설정은 그대로다 */
export function applyDefinition(config: Record<string, any>, def: DataDefinition): Record<string, any> {
  const fromDef = nodeFromDataDefinition(def, config).config;
  // 노드에서 컬럼마다 붙여 둔 설명은 이름이 같으면 살린다
  const oldComments = new Map<string, string>(
    (Array.isArray(config?.tableColumns) ? config.tableColumns : []).map((c: any) => [c?.name, c?.comment || '']),
  );
  fromDef.tableColumns = (fromDef.tableColumns || []).map((c: any) => ({ ...c, comment: oldComments.get(c.name) || c.comment || '' }));
  return fromDef;
}

/** 이 노드가 가리키는 정의와 어긋나 있는가 (정의를 모르면 false — 판단할 수 없다) */
export function isOutOfSync(node: any, def: DataDefinition | undefined): boolean {
  if (!def || !isDataNode(node)) return false;
  const cfg = node.data?.config || {};
  return linkedView(node.type, cfg) !== linkedView(node.type, applyDefinition(cfg, def));
}

/**
 * 노드들을 정의에 맞춘다. 바뀐 노드 id 목록도 돌려준다.
 * `only` 를 주면 그 정의를 가리키는 노드만 본다(한 정의가 바뀌었을 때).
 */
export function syncNodes<N extends { id: string; type?: string; data?: any }>(
  nodes: N[], defs: DataDefinition[], only?: Set<string>,
): { nodes: N[]; changed: string[] } {
  const byId = new Map(defs.map((d) => [d.id, d]));
  const changed: string[] = [];
  const out = nodes.map((n) => {
    if (!isDataNode(n)) return n;
    const id = n.data?.config?.dataDefinitionId;
    if (!id || (only && !only.has(id))) return n;
    const def = byId.get(id);
    if (!def || !isOutOfSync(n, def)) return n;
    changed.push(n.id);
    return { ...n, data: { ...n.data, config: applyDefinition(n.data?.config || {}, def) } };
  });
  return { nodes: changed.length ? out : nodes, changed };
}

/** 정의를 쓰는 곳 — 워크플로마다 노드 수 */
export interface DefinitionUse { projectId: string; workflowId: string; name: string; curVerNo: number; nodes: number }

/**
 * 저장된 캔버스 전체(모든 프로젝트)를 훑는다 — 정의는 프로젝트를 가리지 않고 공유되기 때문이다.
 * `visit` 가 워크플로마다 불린다. 하나를 못 읽어도 멈추지 않는다(실패 목록에 담는다).
 */
async function forEachSavedCanvas(
  visit: (wf: { projectId: string; id: string; name: string; curVerNo: number }, doc: any) => Promise<void> | void,
): Promise<{ failed: Array<{ name: string; error: string }> }> {
  const failed: Array<{ name: string; error: string }> = [];
  const projects = await repo.listProjects();
  for (const p of projects) {
    let workflows: any[] = [];
    try { workflows = await repo.listWorkflows(p.id); } catch (e) { failed.push({ name: p.name, error: (e as Error).message }); continue; }
    for (const w of workflows) {
      if (!w.curVerNo || w.curVerNo < 1) continue;
      try {
        const doc = await repo.loadCanvas(w.id, w.curVerNo);
        if (doc) await visit({ projectId: p.id, id: w.id, name: w.name, curVerNo: w.curVerNo }, doc);
      } catch (e) {
        failed.push({ name: w.name, error: (e as Error).message });
      }
    }
  }
  return { failed };
}

/** 이 정의를 쓰는 저장된 워크플로들 — 지우기 전에 확인한다 */
export async function findDefinitionUses(defId: string): Promise<{ uses: DefinitionUse[]; failed: Array<{ name: string; error: string }> }> {
  const uses: DefinitionUse[] = [];
  const { failed } = await forEachSavedCanvas((wf, doc) => {
    const n = (doc.nodes || []).filter((x: any) => isDataNode(x) && x.data?.config?.dataDefinitionId === defId).length;
    if (n) uses.push({ projectId: wf.projectId, workflowId: wf.id, name: wf.name, curVerNo: wf.curVerNo, nodes: n });
  });
  return { uses, failed };
}
